import { useCallback, useEffect, useRef, useState } from "react";
import { NewReviTherapyApi } from "../../../../../api/revi";

const therapyApi = new NewReviTherapyApi();

/**
 * =============================================================================
 * LAS TERAPIAS DEL TERAPEUTA
 * =============================================================================
 * Antes pedia siempre las diez primeras y no ofrecia nada mas: con treinta
 * terapias, **la numero once no era alcanzable desde ninguna parte del portal**,
 * ni en movil ni en escritorio, y el contador del panel decia diez aunque
 * hubiera treinta.
 *
 * Ahora acumula paginas. `total` viene del servidor, que ya lo devolvia, asi que
 * el contador dice la verdad desde la primera carga aunque solo se hayan traido
 * las diez primeras.
 * =============================================================================
 */
export function useMyTherapies({ accessToken, therapistId, limit = 10 }) {
  const [myTherapies, setMyTherapies] = useState([]);
  const [myTherapiesLoading, setMyTherapiesLoading] = useState(false);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [total, setTotal] = useState(0);

  // La pagina vive en una referencia y no en el estado: solo la lee quien pide
  // la siguiente, y meterla en las dependencias del efecto volveria a cargar la
  // lista entera cada vez que se avanza.
  const pagina = useRef(1);

  const pedir = useCallback(
    async (numeroDePagina) => {
      const data = await therapyApi.getMyTherapies(accessToken, therapistId, {
        page: numeroDePagina,
        limit,
      });
      // El servidor devuelve `totalDocs`, que es como lo nombra mongoose-paginate.
      // Leer `total` daba undefined, caia al numero de terapias traidas y
      // entonces "quedan" salia siempre falso: el boton de cargar mas no se
      // pintaba nunca y la terapia numero once seguia sin ser alcanzable.
      const totalDelServidor =
        data?.pagination?.totalDocs ?? data?.therapies?.length ?? 0;
      setTotal(totalDelServidor);
      return data?.therapies || [];
    },
    [accessToken, therapistId, limit],
  );

  const refreshTherapies = useCallback(async () => {
    if (!accessToken || !therapistId) return;
    setMyTherapiesLoading(true);
    try {
      pagina.current = 1;
      setMyTherapies(await pedir(1));
    } catch (e) {
      console.error(e);
      setMyTherapies([]);
      setTotal(0);
    } finally {
      setMyTherapiesLoading(false);
    }
  }, [accessToken, therapistId, pedir]);

  const cargarMasTerapias = useCallback(async () => {
    if (!accessToken || !therapistId || cargandoMas) return;
    setCargandoMas(true);
    try {
      const siguiente = pagina.current + 1;
      const nuevas = await pedir(siguiente);
      if (nuevas.length) {
        pagina.current = siguiente;
        // Por id, no por posicion: si alguien crea una terapia entre dos
        // peticiones, la lista se desplaza y un elemento saldria dos veces.
        setMyTherapies((previas) => {
          const vistas = new Set(previas.map((t) => t._id));
          return [...previas, ...nuevas.filter((t) => !vistas.has(t._id))];
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCargandoMas(false);
    }
  }, [accessToken, therapistId, pedir, cargandoMas]);

  useEffect(() => {
    refreshTherapies();
  }, [refreshTherapies]);

  return {
    myTherapies,
    myTherapiesLoading,
    refreshTherapies,
    cargarMasTerapias,
    cargandoMasTerapias: cargandoMas,
    // Lo que hay en el servidor, no lo que se ha traido.
    totalTerapias: total,
    quedanTerapias: myTherapies.length < total,
  };
}
